import React, { createContext, PropsWithChildren, useEffect } from 'react';

import * as CookieConsent from 'vanilla-cookieconsent';

import { DEFAULT_FUNC } from 'constants/technical';
import { generateContextHook } from 'utils/helpers/contextHelpers';
import { getMatomoSiteId } from 'utils/getMatomoSiteId';
import { LINKS } from 'components/constants';

interface MatomoDispatchContextType {
  trackEvent: (params: { category: string; action: string; name: string }) => void | undefined;
  trackPageView: () => void | undefined;
  updateConsent: (val: boolean) => void;
}

const MatomoDispatchContext = createContext<MatomoDispatchContextType>({
  trackEvent: DEFAULT_FUNC,
  trackPageView: DEFAULT_FUNC,
  updateConsent: DEFAULT_FUNC,
});

const config = {
  urlBase: LINKS.matomoUrl,
  siteId: getMatomoSiteId(),
};

const MatomoProvider = <T,>({ children }: PropsWithChildren<T>) => {
  useEffect(() => {
    window._paq = window._paq || [];
    const doc = document;
    const scriptElement = doc.createElement('script');
    const scripts = doc.getElementsByTagName('script')[0];

    window._paq.push(['enableHeartBeatTimer']);
    window._paq.push(['requireCookieConsent']);
    if (CookieConsent.acceptedCategory('matomo')) {
      window._paq.push(['setCookieConsentGiven']);
      window._paq.push(['setConsentGiven']);
    } else {
      window._paq.push(['forgetCookieConsentGiven']);
      window._paq.push(['forgetConsentGiven']);
    }
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    window._paq.push(['setTrackerUrl', `${config.urlBase}matomo.php`]);
    window._paq.push(['setSiteId', config.siteId]);
    scriptElement.type = 'text/javascript';
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.src = `${config.urlBase}matomo.js`;

    if (scripts && scripts.parentNode) {
      scripts.parentNode.insertBefore(scriptElement, scripts);
    }
  }, []);

  const dispatchValue: MatomoDispatchContextType = {
    trackEvent: ({ category, action, name }) => {
      if (window._paq === undefined) {
        return;
      }

      window._paq.push(['trackEvent', category, action, name]);
    },
    trackPageView: () => {
      const url = window.location.href;

      if (window._paq === undefined) {
        return;
      }

      window._paq.push(['setCustomUrl', [url]]);
      window._paq.push(['setDocumentTitle', [document.title]]);
      window._paq.push(['trackPageView']);
    },
    updateConsent: val => {
      window._paq.push(val ? ['setCookieConsentGiven'] : ['forgetCookieConsentGiven']);
      window._paq.push(val ? ['setConsentGiven'] : ['forgetConsentGiven']);
    },
  };

  return <MatomoDispatchContext.Provider value={dispatchValue}>{children}</MatomoDispatchContext.Provider>;
};

const useMatomo = generateContextHook(MatomoDispatchContext, 'Matomo');

export { MatomoProvider, useMatomo };
